import { clientDayKey, clientTime, shortDateTime } from '../../shared/dateTime';
import type {
  PlanItem,
  PlanSegment,
  PlanSegmentKind,
  PlanSegmentSource,
  TmsPlanSegment,
} from './vehiclePlanApi';

/**
 * Odcinki paska harmonogramu — teksty do dymków i legendy (czysta logika).
 *
 * Omówienie z PM 01.10.2026: szacunek wykonania pełnym paskiem „z odpoczynkiem
 * kierowcy, dolotem, załadunkiem ok. 2 h, przejazdem i rozładunkiem”. Rachunek
 * jest po stronie API (`vehicle-schedules/plan-segments.ts`, ta sama reguła
 * co zajętość — koniec ostatniego odcinka to koniec paska); tu tylko to, co
 * planista czyta: rodzaj, godziny w czasie polskim, długość, że to szacunek,
 * skąd kilometry i na czym oparty odcinek.
 */

export const SEGMENT_LABELS: Record<PlanSegmentKind, string> = {
  access: 'Dolot',
  loading: 'Załadunek',
  stop: 'Punkty pośrednie',
  drive: 'Przejazd',
  break: 'Przerwa',
  rest: 'Odpoczynek',
  unloading: 'Rozładunek',
};

export const SEGMENT_SOURCE_LABELS: Record<PlanSegmentSource, string> = {
  here: 'HERE (trasa dla ciężarówki)',
  tms: 'TMS (dystans trasy ze zlecenia)',
  szacunek: 'szacunek (linia prosta × mnożnik terenowy)',
};

/** Gdy API nie podało opisu przerwy albo odpoczynku — co najmniej podstawa. */
const PAUSE_FALLBACK: Partial<Record<PlanSegmentKind, string>> = {
  break: 'przerwa wymagana przez 561/2006 albo przepisy o czasie pracy',
  rest: 'odpoczynek wymagany przez 561/2006, liczony bez ulg',
};

/** „2 h 12 min”, „45 min”, „11 h”; ujemne i zero — „0 min”. */
export function durationText(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60_000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** „30.09, 08:00–10:12” w obrębie dnia polskiego, inaczej z dwiema datami. */
export function segmentRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '—';
  return clientDayKey(s) === clientDayKey(e)
    ? `${shortDateTime(start)}–${clientTime(end)}`
    : `${shortDateTime(start)} – ${shortDateTime(end)}`;
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Dymek odcinka: najpierw sam odcinek (rodzaj, godziny, długość, źródło,
 * podstawa), potem `orderTitle` — to samo, co dymek całego paska.
 */
export function segmentTitle(segment: PlanSegment, orderTitle: string): string {
  const span = new Date(segment.end).getTime() - new Date(segment.start).getTime();
  const lines = [
    `${SEGMENT_LABELS[segment.kind]} (szacunek): ${segmentRange(segment.start, segment.end)} · ${durationText(span)}`,
  ];
  if (segment.source) lines.push(`Źródło: ${SEGMENT_SOURCE_LABELS[segment.source]}`);
  const note = segment.note ?? PAUSE_FALLBACK[segment.kind];
  if (note) lines.push(`${capitalize(note)}.`);
  return [...lines, '', orderTitle].join('\n');
}

/** Uwagi do odcinków pozycji — linie dymka paska (brak — nic). */
export function segmentNotesLines(item: Pick<PlanItem, 'segmentNotes'>): string[] {
  return (item.segmentNotes ?? []).map((n) => `Uwaga: ${n}`);
}

/** Czy pozycję rysujemy odcinkami (API je podało), czy jednym paskiem. */
export function hasSegments(item: Pick<PlanItem, 'segments'>): boolean {
  return (item.segments ?? []).length > 0;
}

/** Odcinki w legendzie — w kolejności, w jakiej idą na pasku. */
export const LEGEND_SEGMENTS: ReadonlyArray<{ kind: PlanSegmentKind; label: string }> = [
  { kind: 'access', label: 'dolot' },
  { kind: 'loading', label: 'załadunek, rozładunek' },
  { kind: 'stop', label: 'punkty pośrednie' },
  { kind: 'drive', label: 'przejazd' },
  { kind: 'break', label: 'przerwa' },
  { kind: 'rest', label: 'odpoczynek dobowy (561/2006, bez ulg)' },
];

/**
 * „Zajęte do” do wyświetlenia z nazwanym źródłem (12.10.2026, decyzja
 * użytkownika): „Zajęty do: 01.10, 22:00 (wg planu TMS)” albo „(szacunek)”.
 * Starsze API bez pola — koniec szacunku (`occupancyTo`). Bez daty — `null`.
 */
export function occupiedUntilLine(
  item: Pick<PlanItem, 'occupancyTo' | 'occupiedUntil' | 'occupiedUntilSource' | 'occupancyBasis'>,
): string | null {
  const at = item.occupiedUntil ?? item.occupancyTo;
  if (!at) return null;
  const source =
    item.occupiedUntilSource === 'tmsPlan'
      ? 'wg planu TMS'
      : item.occupancyBasis === 'window'
        ? 'koniec okna zlecenia — trasa nieznana'
        : 'szacunek';
  return `Zajęty do: ${shortDateTime(at)} (${source})`;
}

/** Podpis odcinka „wg planu TMS” w legendzie i dymku. */
export const TMS_PLAN_LABEL = 'Wg planu TMS';

/**
 * Dymek odcinka „wg planu TMS”: godziny, długość, podstawa z API i że to
 * tylko informacja; potem dymek całego zlecenia.
 */
export function tmsPlanTitle(segment: TmsPlanSegment, orderTitle: string): string {
  const span = new Date(segment.end).getTime() - new Date(segment.start).getTime();
  return [
    `${TMS_PLAN_LABEL} (dłużej niż szacunek): ${segmentRange(segment.start, segment.end)} · ${durationText(span)}`,
    `${capitalize(segment.note)}.`,
    '',
    orderTitle,
  ].join('\n');
}
